import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { serviceApiV3 } from '@/api/client';

// ============================================
// Types
// ============================================

export interface ServiceZone {
  id: string;
  zone_id: string;
  name?: string;
  status?: string;
}

export interface ServiceDetail {
  id: string;
  service_name?: string;
  provider_scope?: string;
  status?: string;
  zones: ServiceZone[];
  created_at?: string;
  updated_at?: string;
}

interface Envelope<T> {
  status?: number;
  data: T;
  message?: string;
}

type ServiceDetailEnvelope =
  | Envelope<{ service: ServiceDetail }>
  | Envelope<ServiceDetail>;

// ============================================
// Query keys
// ============================================

export const mediaServiceKeys = {
  detail: (serviceId: string) => ['media', 'service', serviceId] as const,
};

// ============================================
// Helpers
// ============================================

/**
 * The v3 backend can answer HTTP 200 with a soft status in the body:
 * `{ status: 403, data: { provider_scope } }` (no access) or `{ status: 404 }`
 * (no such service) — mirrors legacy `resolveServiceDetail`. Surfaced as this
 * typed error so consoles can redirect / show access-denied messaging.
 */
export class ServiceAccessError extends Error {
  constructor(
    public readonly status: 403 | 404,
    public readonly providerScope?: string,
  ) {
    super(status === 403 ? 'Access denied' : 'Service not found');
    this.name = 'ServiceAccessError';
  }
}

function normalizeServiceDetail(data: ServiceDetailEnvelope): ServiceDetail {
  const inner = (data?.data ?? {}) as Record<string, unknown>;
  const svc = ('service' in inner ? inner.service : inner) as ServiceDetail;
  return {
    ...svc,
    zones: svc.zones ?? [],
  };
}

// ============================================
// Hooks
// ============================================

/**
 * Fetch a service detail record (includes `zones[]`). Handles the legacy
 * dual-envelope shape: `{ data: { service } }` or `{ data }` directly.
 * On 403, the body may carry `data.provider_scope` — the error is surfaced
 * as the query's `error` (axios rejects on 4xx) and the caller inspects it.
 */
export function useServiceDetail(serviceId: string | undefined) {
  return useQuery<ServiceDetail>({
    queryKey: mediaServiceKeys.detail(serviceId ?? ''),
    enabled: !!serviceId,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    // 403/404 (hard or soft) are definitive — don't retry them.
    retry: (failureCount, error) => {
      if (error instanceof ServiceAccessError) return false;
      const status = isAxiosError(error) ? error.response?.status : undefined;
      if (status === 403 || status === 404) return false;
      return failureCount < 2;
    },
    queryFn: async () => {
      const res = await serviceApiV3.get<ServiceDetailEnvelope>(
        `/services/${serviceId}`,
      );
      const soft = Number(res.data?.status);
      if (soft === 403) {
        const inner = res.data?.data as { provider_scope?: string } | undefined;
        throw new ServiceAccessError(403, inner?.provider_scope);
      }
      if (soft === 404) throw new ServiceAccessError(404);
      return normalizeServiceDetail(res.data);
    },
  });
}

